import React, { useEffect, useState, ReactNode } from 'react';
import { createPortal } from 'react-dom';

interface PortalMenuProps {
  buttonRef: React.RefObject<HTMLElement | null>;
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
  className?: string;
  style?: React.CSSProperties;
}

const MENU_HEIGHT_ESTIMATE = 160;

export function PortalMenu({ buttonRef, isOpen, onClose, children, className, style }: PortalMenuProps) {
  const [position, setPosition] = useState<{ top?: number; left?: number, bottom?: number, right?: number } | null>(null);

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const openUpward = spaceBelow < MENU_HEIGHT_ESTIMATE;

      // Always align right edge with the button's right edge
      const left = rect.right;

      if (openUpward) {
        setPosition({
          bottom: window.innerHeight - rect.top + 4,
          right: window.innerWidth - rect.right,
        });
      } else {
        setPosition({
          top: rect.bottom + 4,
          right: window.innerWidth - rect.right,
        });
      }
    }
  }, [isOpen, buttonRef]);

  useEffect(() => {
    if (!isOpen) return;

    const handleInteraction = (e: MouseEvent | TouchEvent) => {
      // We don't close if they interact with the dropdown or the trigger button
      if (buttonRef.current && buttonRef.current.contains(e.target as Node)) {
        return;
      }
      
      const portalMenu = document.getElementById('portal-menu-container');
      if (portalMenu && portalMenu.contains(e.target as Node)) {
         return;
      }
      
      onClose();
    };

    const handleScroll = (e: Event) => {
      // Check if the scroll originated from inside the portal menu
      const portalMenu = document.getElementById('portal-menu-container');
      if (portalMenu && e.target instanceof Node && portalMenu.contains(e.target)) {
        return;
      }
      onClose();
    };

    document.addEventListener('mousedown', handleInteraction);
    document.addEventListener('touchstart', handleInteraction);
    window.addEventListener('scroll', handleScroll, true); // Capture phase

    return () => {
      document.removeEventListener('mousedown', handleInteraction);
      document.removeEventListener('touchstart', handleInteraction);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen, onClose, buttonRef]);

  if (!isOpen || !position) return null;

  return createPortal(
    <div
      id="portal-menu-container"
      className={className}
      style={{
        position: 'fixed',
        ...position,
        ...style,
      }}
    >
      {children}
    </div>,
    document.body
  );
}
